import React, { useState } from 'react'
import { Modal, Button, Form as BSForm } from 'react-bootstrap'
import { SelectLicense, useForm, FormProvider } from 'common/Form'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'react-feather'

function StepSelectLicense({ licenses, onNext, onHide, t }) {
	const methods = useForm()
	const { handleSubmit, watch } = methods
	const selectedLicense = watch('license')

	const onSubmit = handleSubmit(({ license }) => {
		onNext(license)
	})

	return (
		<>
			<Modal.Body>
				<p className="text-muted">{t('license_manager.modal_share.description')}</p>
				<FormProvider {...methods}>
					<SelectLicense
						name="license"
						licenses={licenses}
						placeholder={t('license_manager.modal_share.placeholder')}
						rules={{ required: t('license_manager.modal_share.rule') }}
					/>
				</FormProvider>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="dark" onClick={onHide}>
					{t('license_manager.cancel')}
				</Button>
				<Button
					variant="primary"
					onClick={onSubmit}
					disabled={!selectedLicense}
				>
					{t('license_manager.modal_share.next')}
				</Button>
			</Modal.Footer>
		</>
	)
}

function StepRecipients({ selectedLicense, onShare, onBack, loading, t }) {
	const [message, setMessage] = useState('')
	const [recipients, setRecipients] = useState([''])

	const license = selectedLicense?.license
	const product = license?.product
	const maxRecipients = license?.available || 0

	const licenseLabel = product
		? `${product.name}${license.order ? ` aus Auftrag ${license.order.number}` : ''}${license.subscription ? ` aus Abo ${license.subscription.number}` : ''}`
		: ''

	const canAddMore = recipients.length < maxRecipients

	const addRecipient = () => {
		if (!canAddMore) return
		setRecipients([...recipients, ''])
	}

	const removeRecipient = (index) => {
		setRecipients(recipients.filter((_, i) => i !== index))
	}

	const updateRecipient = (index, value) => {
		const updated = [...recipients]
		updated[index] = value
		setRecipients(updated)
	}

	const validRecipients = recipients
		.filter(r => r.trim() !== '' && r.includes('@'))
		.slice(0, maxRecipients)

	const handleSubmit = () => {
		onShare({
			license: selectedLicense,
			message,
			recipients: validRecipients
		})
	}

	return (
		<>
			<Modal.Body>
				<p className="text-muted">{t('license_manager.modal_share.share_description')}</p>

				<BSForm.Group>
					<BSForm.Label>{t('license_manager.modal_share.license_label')}</BSForm.Label>
					<BSForm.Control
						readOnly
						value={licenseLabel}
						className="bg-light"
					/>
				</BSForm.Group>

				<BSForm.Group>
					<BSForm.Label>{t('license_manager.modal_share.message_label')}</BSForm.Label>
					<BSForm.Control
						as="textarea"
						rows={3}
						value={message}
						onChange={e => setMessage(e.target.value)}
						placeholder={t('license_manager.modal_share.message_placeholder')}
					/>
				</BSForm.Group>

				<BSForm.Group>
					<BSForm.Label>
						{t('license_manager.modal_share.recipients_label')}
						<small className="text-muted ml-2">
							({t('license_manager.modal_share.recipients_remaining', { count: maxRecipients - recipients.length })})
						</small>
					</BSForm.Label>
					{recipients.map((email, index) => (
						<div key={index} className="d-flex mb-2">
							<BSForm.Control
								type="email"
								value={email}
								onChange={e => updateRecipient(index, e.target.value)}
								placeholder={t('license_manager.modal_share.recipient_placeholder', { number: index + 1 })}
							/>
							{recipients.length > 1 && (
								<Button
									variant="outline-danger"
									className="ml-2"
									onClick={() => removeRecipient(index)}
								>
									<Trash2 size={16} />
								</Button>
							)}
						</div>
					))}
					<Button
						variant="primary"
						size="sm"
						onClick={addRecipient}
						disabled={!canAddMore}
					>
						{t('license_manager.modal_share.add_recipient')}
					</Button>
				</BSForm.Group>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="dark" onClick={onBack}>
					{t('license_manager.cancel')}
				</Button>
				<Button
					variant="primary"
					onClick={handleSubmit}
					disabled={loading || validRecipients.length === 0}
				>
					{t('license_manager.modal_share.share')}
				</Button>
			</Modal.Footer>
		</>
	)
}

export default function ModalShareLicense({ show, onHide, licenses, onShare, loading }) {
	const { t } = useTranslation('account')
	const [step, setStep] = useState(1)
	const [selectedLicense, setSelectedLicense] = useState(null)

	const handleClose = () => {
		setStep(1)
		setSelectedLicense(null)
		onHide()
	}

	const handleNext = (license) => {
		setSelectedLicense(license)
		setStep(2)
	}

	const handleBack = () => {
		setStep(1)
	}

	const handleShare = (data) => {
		onShare(data)
	}

	return (
		<Modal show={show} onHide={handleClose} centered size="lg">
			<Modal.Header closeButton>
				<Modal.Title>{t('license_manager.modal_share.title')}</Modal.Title>
			</Modal.Header>
			{step === 1 && (
				<StepSelectLicense
					licenses={licenses}
					onNext={handleNext}
					onHide={handleClose}
					t={t}
				/>
			)}
			{step === 2 && (
				<StepRecipients
					selectedLicense={selectedLicense}
					onShare={handleShare}
					onBack={handleBack}
					loading={loading}
					t={t}
				/>
			)}
		</Modal>
	)
}
